import {
  AddIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  IconButton,
  Text
} from "@chakra-ui/react";
import { FiFilter } from "react-icons/fi";
import { MdDragIndicator } from "react-icons/md";
import { useRef } from "react";
import { StateProps } from "components/pipeline-components/classes/state-machine/state";
import { BLOCKS_BY_OPERATION } from "components/pipeline-components/node-catalog";
import { BlockItem } from "./types";
import { AVAILABLE_BLOCKS } from "./constants";
import {
  BlockPreConfigForm,
  hasPreConfigFields,
  isPreConfigured
} from "./block-pre-config-form";

export function BlockSequencePanel({
  blocks,
  onAdd,
  onRemove,
  onReorder,
  onPreConfigChange,
  onToggleExpand
}: {
  blocks: BlockItem[];
  onAdd: (op: StateProps["operation"]) => void;
  onRemove: (index: number) => void;
  onReorder: (from: number, to: number) => void;
  onPreConfigChange: (index: number, preConfig: Partial<StateProps>) => void;
  onToggleExpand: (index: number) => void;
}) {
  const dragIndex = useRef<number | null>(null);
  const hasOp = (op: StateProps["operation"]) =>
    blocks.some((b) => b.operation === op);

  return (
    <Grid templateColumns="1fr 1fr" gap="20px">
      {/* Left: Available blocks */}
      <Box>
        <Text
          fontSize="13px"
          fontWeight="700"
          color="#69778C"
          letterSpacing="0.05em"
          mb="10px"
        >
          Blocos disponíveis
        </Text>
        <Flex direction="column" gap="6px">
          {AVAILABLE_BLOCKS.map((block) => {
            const added = hasOp(block.operation);
            return (
              <Flex
                key={block.operation}
                align="center"
                justify="space-between"
                p="10px 14px"
                bg="white"
                border="1px solid #E2E8F0"
                borderRadius="8px"
              >
                <Flex align="center" gap="10px">
                  <Box
                    w="28px"
                    h="28px"
                    borderRadius="6px"
                    bg={block.softColor}
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    <Icon
                      as={block.icon}
                      color={block.accentColor}
                      w="14px"
                      h="14px"
                    />
                  </Box>
                  <Box>
                    <Text fontSize="13px" fontWeight="600" color="#2D3748">
                      {block.label}
                    </Text>
                    <Text fontSize="11px" color="#718096">
                      {block.category}
                    </Text>
                  </Box>
                </Flex>
                {added ? (
                  <Flex align="center" gap="4px" color="#3ABB78">
                    <CheckIcon w="11px" />
                    <Text fontSize="12px" fontWeight="600">
                      Adicionado
                    </Text>
                  </Flex>
                ) : (
                  <Button
                    size="xs"
                    variant="outline"
                    leftIcon={<AddIcon w="9px" />}
                    onClick={() => onAdd(block.operation)}
                    colorScheme="blue"
                  >
                    Add
                  </Button>
                )}
              </Flex>
            );
          })}
        </Flex>
      </Box>

      {/* Right: Selected sequence */}
      <Box>
        <Text
          fontSize="13px"
          fontWeight="700"
          color="#69778C"
          letterSpacing="0.05em"
          mb="10px"
        >
          Sequência do modelo{" "}
          <Text as="span" fontSize="11px" fontWeight="400" color="#96A2B3">
            (arraste para reordenar)
          </Text>
        </Text>
        {blocks.length === 0 ? (
          <Flex
            align="center"
            justify="center"
            h="100px"
            border="2px dashed #E2E8F0"
            borderRadius="8px"
            color="#96A2B3"
            fontSize="13px"
          >
            Nenhum bloco adicionado ainda
          </Flex>
        ) : (
          <Flex direction="column" gap="8px">
            {blocks.map((block, index) => {
              const def = BLOCKS_BY_OPERATION[block.operation];
              const configured = isPreConfigured(block.preConfig);
              return (
                <Box
                  key={`${block.operation}-${index}`}
                  border="1px solid #E2E8F0"
                  borderRadius="8px"
                  bg="white"
                  draggable
                  onDragStart={() => {
                    dragIndex.current = index;
                  }}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (
                      dragIndex.current !== null &&
                      dragIndex.current !== index
                    ) {
                      onReorder(dragIndex.current, index);
                      dragIndex.current = null;
                    }
                  }}
                  cursor="grab"
                >
                  <Flex align="center" p="10px 12px" gap="10px">
                    <Icon
                      as={MdDragIndicator}
                      color="#CBD5E0"
                      w="18px"
                      h="18px"
                      flexShrink={0}
                      cursor="grab"
                    />
                    <Flex
                      align="center"
                      justify="center"
                      w="22px"
                      h="22px"
                      borderRadius="50%"
                      bg="#EDF2F7"
                      flexShrink={0}
                    >
                      <Text fontSize="11px" fontWeight="700" color="#4A5568">
                        {index + 1}
                      </Text>
                    </Flex>

                    {def && (
                      <Box
                        w="26px"
                        h="26px"
                        borderRadius="6px"
                        bg={def.softColor}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Icon
                          as={def.icon}
                          color={def.accentColor}
                          w="13px"
                          h="13px"
                        />
                      </Box>
                    )}

                    <Box flex={1} minW={0}>
                      <Text fontSize="13px" fontWeight="600" color="#2D3748">
                        {def?.label ?? block.operation}
                      </Text>
                      {hasPreConfigFields(block.operation) && (
                        <Flex
                          align="center"
                          gap="4px"
                          mt="2px"
                          cursor="pointer"
                          onClick={() => onToggleExpand(index)}
                          _hover={{ opacity: 0.7 }}
                        >
                          {configured ? (
                            <CheckIcon w="10px" color="#3ABB78" />
                          ) : (
                            <Icon
                              as={FiFilter}
                              w="10px"
                              h="10px"
                              color="#718096"
                            />
                          )}
                          <Text
                            fontSize="11px"
                            color={configured ? "#3ABB78" : "#718096"}
                            fontWeight="500"
                          >
                            {configured
                              ? "Pré-configurado"
                              : "Pré-configurar campos"}
                          </Text>
                        </Flex>
                      )}
                    </Box>

                    {hasPreConfigFields(block.operation) && (
                      <IconButton
                        aria-label="toggle config"
                        icon={
                          block.expanded ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        }
                        size="xs"
                        variant="ghost"
                        onClick={() => onToggleExpand(index)}
                      />
                    )}

                    <IconButton
                      aria-label="remover bloco"
                      icon={<CloseIcon w="9px" />}
                      size="xs"
                      variant="ghost"
                      colorScheme="red"
                      onClick={() => onRemove(index)}
                    />
                  </Flex>

                  {block.expanded && hasPreConfigFields(block.operation) && (
                    <Box px="12px" pb="12px">
                      <BlockPreConfigForm
                        operation={block.operation}
                        preConfig={block.preConfig}
                        onChange={(next) => onPreConfigChange(index, next)}
                      />
                    </Box>
                  )}
                </Box>
              );
            })}
          </Flex>
        )}
      </Box>
    </Grid>
  );
}
